import React from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
  Typography,
  IconButton,
  Box,
} from '@material-ui/core';
import clsx from 'clsx';
import { colors } from 'design-system';
import {
  faTriangleExclamation,
  faHexagonXmark,
  faCircleExclamation,
  faCircleCheck,
  faX,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type MessageVariants = 'warning' | 'error' | 'info' | 'success';
const iconSize = 24;

export type MessageBoxProps = {
  message: React.ReactNode;
  variant: MessageVariants;
  onCloseClick?: () => void;
  hideCloseButton?: boolean;
  customStyle?: React.CSSProperties;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
      padding: theme.spacing(2),
      borderRadius: 10,
    },
    successContainer: {
      backgroundColor: '#E8FBF3',
    },
    warningContainer: {
      backgroundColor: '#FFEFD0',
    },
    errorContainer: {
      backgroundColor: '#FFDCE0',
    },
    infoContainer: {
      backgroundColor: colors.onahau,
    },
    text: {
      fontWeight: 400,
      fontSize: 15,
      maxWidth: `calc(100% - ${iconSize + theme.spacing(1)}px)`,
    },
    successText: {
      color: '#0C472C',
    },
    errorText: {
      color: '#910314',
    },
    warningText: {
      color: '#623601',
    },
    infoText: {
      color: colors.madison,
    },
    iconContainer: {
      marginRight: theme.spacing(1),
    },
  }),
);

const MessageBoxSpace = ({
  message,
  variant,
  onCloseClick,
  hideCloseButton,
  customStyle,
}: MessageBoxProps): JSX.Element => {
  const classes = useStyles(variant);
  if (message === '') {
    return <div />;
  }

  const setVariantClass = (): string => {
    switch (variant) {
      case 'error': {
        return classes.errorContainer;
      }
      case 'success': {
        return classes.successContainer;
      }
      case 'warning': {
        return classes.warningContainer;
      }
      default: {
        return classes.infoContainer;
      }
    }
  };

  const setTextClass = (): string => {
    switch (variant) {
      case 'error': {
        return classes.errorText;
      }
      case 'success': {
        return classes.successText;
      }
      case 'warning': {
        return classes.warningText;
      }
      default: {
        return classes.infoText;
      }
    }
  };

  const getIconColor = (): string => {
    switch (variant) {
      case 'success': {
        return '#27AE60';
      }
      case 'error': {
        return '#B80016';
      }
      case 'warning': {
        return '#E37E07';
      }
      default: {
        return colors.madison;
      }
    }
  };

  const renderIcon = (): JSX.Element => {
    switch (variant) {
      case 'success': {
        return (
          <FontAwesomeIcon
            data-testid="success-icon"
            icon={faCircleCheck}
            fontSize={24}
            color="#27AE60"
          />
        );
      }
      case 'error': {
        return (
          <FontAwesomeIcon
            data-testid="error-icon"
            icon={faHexagonXmark}
            fontSize={24}
            color="#B80016"
          />
        );
      }
      case 'warning': {
        return (
          <FontAwesomeIcon
            data-testid="warning-icon"
            icon={faTriangleExclamation}
            fontSize={24}
            color="#E37E07"
          />
        );
      }
      default: {
        return (
          <FontAwesomeIcon
            data-testid="info-icon"
            icon={faCircleExclamation}
            fontSize={24}
            color={colors.madison}
          />
        );
      }
    }
  };
  return (
    <div
      data-testid="container"
      className={clsx(classes.container, setVariantClass())}
      style={customStyle}
    >
      <Box display="flex" sx={{ width: '100%' }} alignItems="center">
        <Box sx={{ marginRight: '10px' }}>{renderIcon()}</Box>
        <Box flexGrow={1} sx={{ marginRight: '10px' }}>
          <Typography
            variant="caption"
            className={clsx(classes.text, setTextClass())}
          >
            {message}
          </Typography>
        </Box>
        <Box>
          {!hideCloseButton && (
            <IconButton data-testid="close-button" onClick={onCloseClick}>
              <FontAwesomeIcon
                data-testid="close-icon"
                icon={faX}
                fontSize={17}
                color={getIconColor()}
              />
            </IconButton>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default MessageBoxSpace;
